import React, { useState, useEffect, useCallback } from 'react';
import { 
  Layout, 
  Input, 
  Row, 
  Col, 
  Card, 
  Typography, 
  Pagination, 
  Space, 
  Statistic,
  List,
  Avatar,
  Tag,
  message
} from 'antd';
import { 
  SearchOutlined, 
  FileTextOutlined, 
  UserOutlined, 
  CalendarOutlined,
  EyeOutlined,
  LikeOutlined,
  MessageOutlined
} from '@ant-design/icons';
import PostCard from '../../components/PostCard';
import { posts } from '../../api/mockData';
import './index.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

const HomePage = () => {
  const [allPosts, setAllPosts] = useState([]);
  const [displayPosts, setDisplayPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(6);

  const handleSearch = useCallback((value) => {
    const query = value || searchQuery;
    if (!query.trim()) {
      setDisplayPosts(allPosts);
      return;
    }
    
    const filtered = allPosts.filter(post => 
      post.title.toLowerCase().includes(query.toLowerCase()) ||
      post.summary.toLowerCase().includes(query.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase())) ||
      post.author.toLowerCase().includes(query.toLowerCase())
    );
    
    setDisplayPosts(filtered);
    setCurrentPage(1);
    
    if (filtered.length === 0) {
      message.info('没有找到相关文章');
    }
  }, [allPosts, searchQuery]);

  useEffect(() => {
    // 初始化文章数据
    setAllPosts(posts);
    setDisplayPosts(posts);
    
    // 检查URL中的搜索参数
    const params = new URLSearchParams(window.location.search);
    const searchParam = params.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      handleSearch(searchParam);
    }
  }, [handleSearch]);

  const getStats = useCallback(() => {
    return {
      totalPosts: allPosts.length,
      totalViews: allPosts.reduce((sum, post) => sum + post.readCount, 0),
      totalLikes: allPosts.reduce((sum, post) => sum + post.likes, 0),
      totalTags: [...new Set(allPosts.flatMap(post => post.tags))].length
    };
  }, [allPosts]);

  const getRecentComments = useCallback(() => {
    // 模拟评论数据
    return [
      {
        id: 1,
        user: '张三',
        avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
        content: '这篇文章很有帮助，谢谢分享！',
        postTitle: 'Linux负载累增的检查命令',
        time: '2小时前'
      },
      {
        id: 2,
        user: '李四',
        avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
        content: 'JDK下载确实需要注意官方源，感谢提醒',
        postTitle: 'JDK各版本官方下载地址',
        time: '5小时前'
      },
      {
        id: 3,
        user: '王五',
        avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
        content: '武汉美食攻略很实用，收藏了！',
        postTitle: '武汉美食打卡全攻略',
        time: '1天前'
      }
    ];
  }, []);

  const stats = getStats();
  const recentComments = getRecentComments();
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPosts = displayPosts.slice(startIndex, endIndex);

  return (
    <Layout className="home-page">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <Title className="hero-title">好奇分享站</Title>
          <Text className="hero-subtitle">
            分享技术干货，记录学习点滴，探索无限可能
          </Text>
          <div className="search-container">
            <Search
              placeholder="搜索文章、标签、作者..."
              enterButton={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={handleSearch}
              className="search-input"
            />
          </div>
        </div>
      </div>

      <Content className="content-section">
        {/* 统计卡片 */}
        <Row gutter={[24, 24]} className="stats-cards">
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <FileTextOutlined className="stat-icon" style={{ color: '#1890ff' }} />
              <Statistic
                className="stat-number"
                value={stats.totalPosts}
                suffix="篇"
              />
              <Text className="stat-label">总文章数</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <EyeOutlined className="stat-icon" style={{ color: '#52c41a' }} />
              <Statistic
                className="stat-number"
                value={stats.totalViews}
              />
              <Text className="stat-label">总阅读量</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <LikeOutlined className="stat-icon" style={{ color: '#f5222d' }} />
              <Statistic
                className="stat-number"
                value={stats.totalLikes}
              />
              <Text className="stat-label">总点赞数</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card className="stat-card">
              <Tag className="stat-icon" style={{ fontSize: '36px', color: '#722ed1' }} />
              <Statistic
                className="stat-number"
                value={stats.totalTags}
                suffix="个"
              />
              <Text className="stat-label">标签数量</Text>
            </Card>
          </Col>
        </Row>

        {/* 推荐文章 */}
        <div className="featured-posts">
          <Title level={3} className="section-title">
            <FileTextOutlined /> 最新文章
          </Title>
          {currentPosts.length > 0 ? (
            <>
              <div className="posts-grid">
                {currentPosts.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </div>
              
              <div className="pagination-container">
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={displayPosts.length}
                  onChange={(page) => setCurrentPage(page)}
                  showSizeChanger={false}
                  showTotal={(total) => `共 ${total} 篇文章`}
                />
              </div>
            </>
          ) : (
            <div className="no-posts">
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <Title level={4} style={{ color: '#999' }}>
                暂无文章
              </Title>
              <Text type="secondary">尝试搜索其他关键词</Text>
            </div>
          )}
        </div>

        {/* 最近评论 */}
        <div className="recent-comments">
          <Title level={3} className="section-title">
            <MessageOutlined /> 最近评论
          </Title>
          <List
            dataSource={recentComments}
            renderItem={comment => (
              <List.Item className="comment-item">
                <List.Item.Meta
                  avatar={<Avatar src={comment.avatar} icon={<UserOutlined />} />}
                  title={
                    <Space>
                      <Text strong>{comment.user}</Text>
                      <Text type="secondary">评论了</Text>
                      <Text type="primary">{comment.postTitle}</Text>
                    </Space>
                  }
                  description={
                    <>
                      <div className="comment-content">{comment.content}</div>
                      <div className="comment-meta">
                        <span>{comment.time}</span>
                        <Space>
                          <CalendarOutlined />
                          {new Date().toLocaleDateString()}
                        </Space>
                      </div>
                    </>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      </Content>
    </Layout>
  );
};

export default HomePage;